import { defineMessages } from 'react-intl';
import { ResultCategories, getColorForQuestionnaireType } from './Results';
import { ReactComponent as ResultsHaze } from './weather-haze-results-0.svg';
import { ReactComponent as ResultsSunny } from './weather-sunny-results-20.svg';
import { ReactComponent as ResultsPartlyCloudy } from './weather-partly-cloudy-day-results-40.svg';
import { ReactComponent as ResultsCloudy } from './weather-cloudy-results-60.svg';
import { ReactComponent as ResultsDrizzle } from './weather-drizzle-results-80.svg';
import { ReactComponent as ResultsThunderstorm } from './weather-thunderstorm-results-100.svg';

export const calculateResultAlgorithmForCategory = (intl, category, score) => {
  const resultTitles = defineMessages({
    resultsCardPain: {
      id: 'results.card.pain',
      defaultMessage: 'Pain',
      description: 'Results page pain card text',
    },
    resultsCardWorry: {
      id: 'results.card.worry',
      defaultMessage: 'Worry',
      description: 'Results page worry card text',
    },
    resultsCardMood: {
      id: 'results.card.mood',
      defaultMessage: 'Mood',
      description: 'Results page mood card text',
    },
    resultsCardSleep: {
      id: 'results.card.sleep',
      defaultMessage: 'Sleep',
      description: 'Results page sleep card text',
    },
    resultsCardSubstanceAbuse: {
      id: 'results.card.substance_abuse',
      defaultMessage: 'Substance Use',
      description: 'Results page substance abuse card text',
    },
    resultsCardNotesTitle: {
      id: 'results.card.notes.title',
      defaultMessage: 'Your Notes',
      description: 'Results page notes card title',
    },
    resultCardNotesPlaceholder: {
      id: 'results.card.notes.placeholder',
      defaultMessage: 'Click here to add notes...',
      description: 'Results page notes card placeholder text',
    },
  });

  const painAlgorithmMessages = defineMessages({
    pain55AndLower: {
      id: 'results.pain55AndLower',
      defaultMessage:
        "Your score indicates that you are coping well with your pain. Consider what you are doing that's working, and do more of it!",
      description: 'Algorithm result pain55AndLower',
    },
    pain60or65: {
      id: 'results.pain60or65',
      defaultMessage:
        'Your score indicates that you may have small problems coping with your pain. There are tools and resources available that can help you to better manage it.',
      description: 'Algorithm result pain60or65',
    },
    pain70To95: {
      id: 'results.pain70To95',
      defaultMessage:
        'Your score indicates that you may have some problems coping with your pain. There are tools and resources available that can help you to better manage it.',
      description: 'Algorithm result pain70To95',
    },
    pain100: {
      id: 'results.pain100',
      defaultMessage:
        "Your score indicates that you may be having a difficult time coping with your pain. Let's get you some support! Check out these pain education resources and mobile interventions.",
      description: 'Algorithm result pain100',
    },
  });

  const worryAlgorithmMessages = defineMessages({
    worry50orLower: {
      id: 'results.worry50orLower',
      defaultMessage:
        "Your score indicates worry is not a problem for you. You are coping well, keep doing what's working for you! Feel free to look into some educational resources on worry for more information.",
      description: 'Algorithm result worry50orLower',
    },
    worry55to60: {
      id: 'results.worry55to60',
      defaultMessage:
        'Your score indicates that you may have small problems coping with worry. There are tools and resources available that can help you.',
      description: 'Algorithm result worry55to60',
    },
    worry65to80: {
      id: 'results.worry65to80',
      defaultMessage:
        'Your score indicates that you may have some problems coping with worry. There are tools and resources available that can help you.',
      description: 'Algorithm result worry65to80',
    },
    worry85to100: {
      id: 'results.worry85to100',
      defaultMessage:
        "Your score indicates that you may be having a difficult time coping with worry. Let's get you some support! Check out these education resources and mobile interventions.",
      description: 'Algorithm result worry85to100',
    },
  });

  const moodAlgorithmMessages = defineMessages({
    mood45orLower: {
      id: 'results.mood45orLower',
      defaultMessage:
        "Your score indicates your mood is not a problem for you. You are coping well, keep doing what's working for you! Feel free to look into some educational resources on mood for more information.",
      description: 'Algorithm result mood45orLower',
    },
    mood50to55: {
      id: 'results.mood50to55',
      defaultMessage:
        'Your score indicates that you may have small problems coping with your mood. There are tools and resources available that can help you.',
      description: 'Algorithm result mood50to55',
    },
    mood60to80: {
      id: 'results.mood60to80',
      defaultMessage:
        'Your score indicates that you may have some problems coping with your mood. There are some tools and resources available that can help you.',
      description: 'Algorithm result mood60to80',
    },
    mood85to100: {
      id: 'results.mood85to100',
      defaultMessage:
        "Your score indicates that you may be having a difficult time coping with your mood or feelings. Let's get you some support! Check out these education resources and mobile interventions.",
      description: 'Algorithm result mood85to100',
    },
  });

  const sleepAlgorithmMessages = defineMessages({
    sleep0to25: {
      id: 'results.sleep0to25',
      defaultMessage:
        'Your score indicates sleep is not a problem for you. Keep doing what’s working for you!',
      description: 'Algorithm result sleep0to25',
    },
    sleep29to50: {
      id: 'results.sleep29to50',
      defaultMessage:
        'Your score indicates sleep might be a small problem for you. Are there some things you can do to adapt to improve your sleep?',
      description: 'Algorithm result sleep29to50',
    },
    sleep54to75: {
      id: 'results.sleep54to75',
      defaultMessage:
        'Your score indicates that you may have some problems with your sleep. Think about what’s been getting in the way of a good night’s sleep and see if the tools and resources here might help.',
      description: 'Algorithm result sleep54to75',
    },
    sleep79to100: {
      id: 'results.sleep79to100',
      defaultMessage:
        'Your score indicates that you may be having a difficult time sleeping. Think about what’s been getting in the way of a good night’s sleep and see if the playlists or mobile interventions here can help.',
      description: 'Algorithm result sleep79to100',
    },
  });

  const substanceAlgorithmMessages = defineMessages({
    substanceUndefined: {
      id: 'results.substanceUndefined',
      defaultMessage:
        'Looks like you skipped the Substance Use section of the questionnaire. Without it we are unable to calculate a substance use score, or provide any recommendations for this category.',
      description: 'Algorithm result substanceUndefined',
    },
    substanceNoScore: {
      id: 'results.substanceNoScore',
      defaultMessage: 'No Score',
      description: 'Algorithm result no score',
    },
    substance0: {
      id: 'results.substance0',
      defaultMessage:
        'Your score suggests that substance use behaviours may not be a problem for you. You are coping well, keep doing what’s working for you! Feel free to look into some educational resources on substance use for more information.',
      description: 'Algorithm result substance0',
    },
    substance0to25: {
      id: 'results.substance0to25',
      defaultMessage:
        'Overall, in the past two weeks, you were fairly confident in resisting using drugs and alcohol in several high-risk situations. There are some tools and educational resources available that can help you learn more about managing substance use and its triggers.',
      description: 'Algorithm result substance0to25',
    },
    substance26to50: {
      id: 'results.substance26to50',
      defaultMessage:
        'Overall, in the past two weeks you were moderately confident in resisting using drugs and alcohol in high-risk situations. Try checking out the educational resources and tools for managing both substance use and its common triggers.',
      description: 'Algorithm result substance26to50',
    },
    substance51to75: {
      id: 'results.substance51to75',
      defaultMessage:
        'Over the past two weeks, you were somewhat confident in resisting using drugs and alcohol in high-risk situations. Let’s get you some support and increase your confidence in these situations! Check out the educational resources and tools for managing both substance use and its common triggers.',
      description: 'Algorithm result substance51to75',
    },
    substance76to100: {
      id: 'results.substance76to100',
      defaultMessage:
        '	Over the past two weeks, you did not feel very confident in resisting using drugs and alcohol in high-risk situations. Let’s get you some support and increase your confidence in these situations. Check out the educational resources and tools for managing both substance use and its common triggers.',
      description: 'Algorithm result substance76to100',
    },
  });

  // Helper to create the results.
  const createAlgorithmResultForCategory = (title, color, score, message, icon) => {
    return {
      title,
      color,
      score,
      message,
      icon,
    };
  };

  const createNotesResultForCategory = (title, color, message, isNotesCard) => {
    return {
      title,
      color,
      message,
      isNotesCard,
    };
  };

  switch (category) {
    case ResultCategories.PAIN:
      if (score <= 55) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardPain),
          getColorForQuestionnaireType(ResultCategories.PAIN),
          score,
          intl.formatMessage(painAlgorithmMessages.pain55AndLower),
          ResultsSunny,
        );
      } else if (score >= 56 && score <= 69) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardPain),
          getColorForQuestionnaireType(ResultCategories.PAIN),
          score,
          intl.formatMessage(painAlgorithmMessages.pain60or65),
          ResultsPartlyCloudy,
        );
      } else if (score >= 70 && score < 100) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardPain),
          getColorForQuestionnaireType(ResultCategories.PAIN),
          score,
          intl.formatMessage(painAlgorithmMessages.pain70To95),
          ResultsCloudy,
        );
      } else {
        // Has to be 100.
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardPain),
          getColorForQuestionnaireType(ResultCategories.PAIN),
          score,
          intl.formatMessage(painAlgorithmMessages.pain100),
          ResultsDrizzle,
        );
      }
    case ResultCategories.WORRY:
      if (score <= 50) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardWorry),
          getColorForQuestionnaireType(ResultCategories.WORRY),
          score,
          intl.formatMessage(worryAlgorithmMessages.worry50orLower),
          ResultsSunny,
        );
      } else if (score >= 51 && score <= 64) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardWorry),
          getColorForQuestionnaireType(ResultCategories.WORRY),
          score,
          intl.formatMessage(worryAlgorithmMessages.worry55to60),
          ResultsPartlyCloudy,
        );
      } else if (score >= 65 && score <= 84) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardWorry),
          getColorForQuestionnaireType(ResultCategories.WORRY),
          score,
          intl.formatMessage(worryAlgorithmMessages.worry65to80),
          ResultsCloudy,
        );
      } else {
        // Score is 85 or above.
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardWorry),
          getColorForQuestionnaireType(ResultCategories.WORRY),
          score,
          intl.formatMessage(worryAlgorithmMessages.worry85to100),
          ResultsDrizzle,
        );
      }
    case ResultCategories.MOOD:
      if (score <= 45) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardMood),
          getColorForQuestionnaireType(ResultCategories.MOOD),
          score,
          intl.formatMessage(moodAlgorithmMessages.mood45orLower),
          ResultsSunny,
        );
      } else if (score >= 46 && score <= 59) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardMood),
          getColorForQuestionnaireType(ResultCategories.MOOD),
          score,
          intl.formatMessage(moodAlgorithmMessages.mood50to55),
          ResultsPartlyCloudy,
        );
      } else if (score >= 60 && score <= 84) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardMood),
          getColorForQuestionnaireType(ResultCategories.MOOD),
          score,
          intl.formatMessage(moodAlgorithmMessages.mood60to80),
          ResultsCloudy,
        );
      } else {
        // Results are 85 to 100
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardMood),
          getColorForQuestionnaireType(ResultCategories.MOOD),
          score,
          intl.formatMessage(moodAlgorithmMessages.mood85to100),
          ResultsDrizzle,
        );
      }
    case ResultCategories.SLEEP:
      if (score <= 41) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardSleep),
          getColorForQuestionnaireType(ResultCategories.SLEEP),
          score,
          intl.formatMessage(sleepAlgorithmMessages.sleep0to25),
          ResultsSunny,
        );
      } else if (score >= 42 && score <= 61) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardSleep),
          getColorForQuestionnaireType(ResultCategories.SLEEP),
          score,
          intl.formatMessage(sleepAlgorithmMessages.sleep29to50),
          ResultsPartlyCloudy,
        );
      } else if (score >= 62 && score <= 81) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardSleep),
          getColorForQuestionnaireType(ResultCategories.SLEEP),
          score,
          intl.formatMessage(sleepAlgorithmMessages.sleep54to75),
          ResultsCloudy,
        );
      } else {
        // Anything more than 81.
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardSleep),
          getColorForQuestionnaireType(ResultCategories.SLEEP),
          score,
          intl.formatMessage(sleepAlgorithmMessages.sleep79to100),
          ResultsDrizzle,
        );
      }
    case ResultCategories.SUBSTANCE:
      if (score === undefined) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardSubstanceAbuse),
          getColorForQuestionnaireType(ResultCategories.SUBSTANCE),
          intl.formatMessage(substanceAlgorithmMessages.substanceNoScore),
          intl.formatMessage(substanceAlgorithmMessages.substanceUndefined),
          ResultsHaze,
        );
      } else if (score === -1) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardSubstanceAbuse),
          getColorForQuestionnaireType(ResultCategories.SUBSTANCE),
          '0',
          intl.formatMessage(substanceAlgorithmMessages.substance0),
          ResultsSunny,
        );
      } else if (score >= 0 && score <= 25) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardSubstanceAbuse),
          getColorForQuestionnaireType(ResultCategories.SUBSTANCE),
          score,
          intl.formatMessage(substanceAlgorithmMessages.substance0to25),
          ResultsPartlyCloudy,
        );
      } else if (score >= 26 && score <= 50) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardSubstanceAbuse),
          getColorForQuestionnaireType(ResultCategories.SUBSTANCE),
          score,
          intl.formatMessage(substanceAlgorithmMessages.substance26to50),
          ResultsCloudy,
        );
      } else if (score >= 51 && score <= 75) {
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardSubstanceAbuse),
          getColorForQuestionnaireType(ResultCategories.SUBSTANCE),
          score,
          intl.formatMessage(substanceAlgorithmMessages.substance51to75),
          ResultsDrizzle,
        );
      } else {
        // Score is > 75 and not undefined or -1.
        return createAlgorithmResultForCategory(
          intl.formatMessage(resultTitles.resultsCardSubstanceAbuse),
          getColorForQuestionnaireType(ResultCategories.SUBSTANCE),
          score,
          intl.formatMessage(substanceAlgorithmMessages.substance76to100),
          ResultsThunderstorm,
        );
      }
    case ResultCategories.NOTES:
      return createNotesResultForCategory(
        intl.formatMessage(resultTitles.resultsCardNotesTitle),
        getColorForQuestionnaireType(ResultCategories.NOTES),
        intl.formatMessage(resultTitles.resultCardNotesPlaceholder),
        true,
      );
    default:
      return {};
  }
};
